import { EWorksheetDecision } from '@if/core/types'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { MechanicalWarrantyComputeResultDto } from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/ComputeMechanicalWarrantyResultDto'
import {
  MechanicalWarrantyWorksheet,
  MechanicalWarrantyWorksheetRevision,
} from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/mechanicalWarrantyWorksheetSchema'
import { NormsMessage } from '@src/data/types'
import { FinancingProgramProvider } from '@src/data/types/FinancingProgramProvider'
import { MechanicalWarrantyProduct } from '@src/data/types/MechanicalWarrantyProduct'
import { ParameterizedMessage } from '@src/data/types/ParameterizedMessage'
import translate from '@src/services/translate'
import { TRootState } from '..'
import { apiStateSelectors } from '../ApiStateStore'
import { appSelectors } from '../AppStore'

export const MechanicalWarrantyWorksheetSlice = createSlice({
  name: 'mechanicalWarrantyWorksheet',
  initialState: {
    current: null as MechanicalWarrantyWorksheet | null,
    computedInfo: {} as MechanicalWarrantyComputeResultDto,
    currentRevisions: [] as MechanicalWarrantyWorksheetRevision[],
    mechanicalWarrantyProviders: [] as FinancingProgramProvider[],
    mechanicalWarrantyProviderProducts: [] as MechanicalWarrantyProduct[],
  },
  reducers: {
    setCurrent: (state, action: PayloadAction<MechanicalWarrantyWorksheet | null>) => {
      state.current = action.payload
    },
    setLoanComputed: (state, action: PayloadAction<MechanicalWarrantyComputeResultDto | null>) => {
      if (action.payload)
        state.computedInfo = {
          ...state.computedInfo,
          ...action.payload,
        }
    },
    setCurrentRevisions: (state, action: PayloadAction<MechanicalWarrantyWorksheetRevision[]>) => {
      state.currentRevisions = action.payload.sort((a, b) => {
        if (!a.updatedOn) return -1
        if (!b.updatedOn) return 1
        return a.updatedOn < b.updatedOn ? 1 : -1
      })
    },

    setProviders: (state, action: PayloadAction<FinancingProgramProvider[]>) => {
      state.mechanicalWarrantyProviders = action.payload
    },
    setProductsByProvider: (state, action: PayloadAction<MechanicalWarrantyProduct[]>) => {
      state.mechanicalWarrantyProviderProducts = action.payload
    },
  },
})

export default MechanicalWarrantyWorksheetSlice.reducer

export const mechanicalWarrantyWorksheetActions = MechanicalWarrantyWorksheetSlice.actions

export const GET_MECHANICAL_WARRANTY_WORKSHEET = 'mechanicalWarrantyWorksheet/getById'
export const SAVE_MECHANICAL_WARRANTY_WORKSHEET = 'mechanicalWarrantyWorksheet/save'
export const COMPUTE_MECHANICAL_WARRANTY_LOAN = 'mechanicalWarrantyWorksheet/computeMechanicalWarrantyLoan'
export const GET_MECHANICAL_WARRANTY_WORKSHEET_REVISIONS = 'mechanicalWarrantyWorksheet/revisions'
export const SAVE_MECHANICAL_WARRANTY_WORKSHEET_DECISION = 'mechanicalWarrantyWorksheet/Decision'
export const GET_MECHANICAL_WARRANTY_PROVIDER = 'mechanicalWarrantyWorksheet/provider'
export const GET_MECHANICAL_WARRANTY_PROVIDER_PRODUCTS = 'mechanicalWarrantyWorksheet/provider/products'

const mechanicalWarrantyWorksheetSelectorsPartial = {
  getCurrent: (state: TRootState) => state.mechanicalWarrantyWorksheet.current,
  isComputingMechanicalWarrantyLoan: (state: TRootState) =>
    apiStateSelectors.isLoading(state, COMPUTE_MECHANICAL_WARRANTY_LOAN),
  getComputedInfo: (state: TRootState) => state.mechanicalWarrantyWorksheet.computedInfo,
  getCurrentRevisions: (state: TRootState) => state.mechanicalWarrantyWorksheet.currentRevisions,
  getProviders: (state: TRootState) => state.mechanicalWarrantyWorksheet.mechanicalWarrantyProviders,
  getProductsByProvider: (state: TRootState) => state.mechanicalWarrantyWorksheet.mechanicalWarrantyProviderProducts,
  isLoadingProductsOfProvider: (state: TRootState) =>
    apiStateSelectors.isLoading(state, GET_MECHANICAL_WARRANTY_PROVIDER_PRODUCTS),
  isLoadingProviders: (state: TRootState) => apiStateSelectors.isLoading(state, GET_MECHANICAL_WARRANTY_PROVIDER),
  worksheetIsApproved: (state: TRootState) =>
    state.mechanicalWarrantyWorksheet.current?.finalDecision.decision === EWorksheetDecision.Approved,
}

function getParameterizedNormMessage(message: ParameterizedMessage): string {
  const translatedMessage = translate([`worksheet.norms.${message.id}`])

  let messageSuffix = ' | '
  message.parameters.forEach((p, i) => {
    if (i >= 1) messageSuffix = messageSuffix.concat(', ')

    const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
    messageSuffix = messageSuffix.concat(suffix)
  })

  return translatedMessage.concat(messageSuffix)
}

const getCurrentWorksheetNormWarningMessages = createSelector(
  [mechanicalWarrantyWorksheetSelectorsPartial.getCurrent, appSelectors.getCurrentLang],
  (currentWorksheet) => {
    const worksheetNormMessages: NormsMessage[] = []
    currentWorksheet?.normsDecision?.warningMessages.forEach((wr) => {
      const builtMessage = getParameterizedNormMessage(wr)
      const worksheetNormMessage: NormsMessage = {
        id: wr.id,
        message: builtMessage,
        applicantType: null,
      }
      worksheetNormMessages.push(worksheetNormMessage)
    })

    return worksheetNormMessages
  },
)

const getCurrentWorksheetNormRefusalAndWarningMessages = createSelector(
  [mechanicalWarrantyWorksheetSelectorsPartial.getCurrent, appSelectors.getCurrentLang],
  (currentWorksheet) => {
    const warningsAndRefusals = currentWorksheet?.normsDecision?.warningMessages?.concat(
      currentWorksheet?.normsDecision?.refusalMessages,
    )

    const worksheetNormMessages: NormsMessage[] = []
    warningsAndRefusals?.forEach((wr) => {
      const builtMessage = getParameterizedNormMessage(wr)
      const worksheetNormMessage: NormsMessage = {
        id: wr.id,
        message: builtMessage,
        applicantType: null,
      }
      worksheetNormMessages.push(worksheetNormMessage)
    })
    return worksheetNormMessages
  },
)

export const mechanicalWarrantyWorksheetSelectors = {
  ...mechanicalWarrantyWorksheetSelectorsPartial,
  getCurrentWorksheetNormRefusalAndWarningMessages,
  getCurrentWorksheetNormWarningMessages,
}
